import { useCallback, useEffect, useState } from "react";
import Lenis from "lenis";
import axios from "axios";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { setLenis, scrollToId } from "@/lib/scroll";
import { SiteSettings } from "@/lib/siteSettings";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { SearchBox } from "@/components/SearchBox";
import { QuickServices } from "@/components/QuickServices";
import { FeaturedProperties } from "@/components/FeaturedProperties";
import { Categories } from "@/components/Categories";
import { Marquee } from "@/components/Marquee";
import { WhyUs } from "@/components/WhyUs";
import { Projects } from "@/components/Projects";
import { About } from "@/components/About";
import { Team } from "@/components/Team";
import { HowItWorks } from "@/components/HowItWorks";
import { InvestmentCTA } from "@/components/InvestmentCTA";
import { Testimonials } from "@/components/Testimonials";
import { LeadSection } from "@/components/LeadSection";
import { WhatsAppStrip } from "@/components/WhatsAppStrip";
import { FinalCTA } from "@/components/FinalCTA";
import { Footer } from "@/components/Footer";
import { MobileBar } from "@/components/MobileBar";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import PropertyPage from "@/pages/PropertyPage";
import PropertyForm from "@/pages/admin/PropertyForm";
import ProjectForm from "@/pages/admin/ProjectForm";
import TeamForm from "@/pages/admin/TeamForm";

const SETTINGS_URL = `${process.env.REACT_APP_BACKEND_URL}/api/settings`;

const HomePage = () => {
  const [filters, setFilters] = useState(null);
  const [prefill, setPrefill] = useState(null);

  const search = useCallback((f) => setFilters(f), []);
  const enquire = useCallback((p) => {
    setPrefill({ ...p, nonce: Date.now() });
    scrollToId("#enquire");
  }, []);

  useEffect(() => {
    const pre = sessionStorage.getItem("rk_prefill");
    if (pre) {
      try {
        setPrefill({ ...JSON.parse(pre), nonce: Date.now() });
      } catch {}
      sessionStorage.removeItem("rk_prefill");
    }
    const target = sessionStorage.getItem("rk_scroll_to");
    if (target) sessionStorage.removeItem("rk_scroll_to");
    const scrollTarget = pre ? "#enquire" : target;
    if (scrollTarget) setTimeout(() => scrollToId(scrollTarget), 600);
  }, []);

  return (
    <div className="App bg-warm pb-[64px] md:pb-0">
      <Header />
      <main>
        <Hero />
        <SearchBox onSearch={search} onEnquire={enquire} />
        <QuickServices onSearch={search} onEnquire={enquire} />
        <FeaturedProperties filters={filters} onClear={() => setFilters(null)} onEnquire={enquire} />
        <Categories onExplore={search} />
        <Marquee />
        <WhyUs />
        <Projects onEnquire={enquire} />
        <About />
        <Team />
        <HowItWorks />
        <InvestmentCTA />
        <Testimonials />
        <LeadSection prefill={prefill} />
        <WhatsAppStrip />
        <FinalCTA />
      </main>
      <Footer />
      <MobileBar />
    </div>
  );
};

function App() {
  const [site, setSite] = useState(null);

  const refreshSettings = useCallback(() => {
    return axios
      .get(SETTINGS_URL)
      .then(({ data }) => setSite(data))
      .catch(() => setSite({}));
  }, []);

  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    setLenis(lenis);
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      setLenis(null);
    };
  }, []);

  return (
    <SiteSettings.Provider value={{ site, refresh: refreshSettings }}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/property/:id" element={<PropertyPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/properties/new" element={<PropertyForm />} />
          <Route path="/admin/properties/:id/edit" element={<PropertyForm />} />
          <Route path="/admin/projects/new" element={<ProjectForm />} />
          <Route path="/admin/projects/:id/edit" element={<ProjectForm />} />
          <Route path="/admin/team/new" element={<TeamForm />} />
          <Route path="/admin/team/:id/edit" element={<TeamForm />} />
        </Routes>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </SiteSettings.Provider>
  );
}

export default App;
